import { useEffect, useMemo, useState } from "react";
import DirectRewardsRedemptionOutcome from "../../../../components/campaign/rulesAndOutcomes/outcomes/directRewardsRedemption/DirectRewardsRedemptionOutcome";
import { RetailerConditionDisplay } from "../../../../components/campaign/rulesAndOutcomes/outcomes/retailerOutcome";
import "../../../../components/campaign/rulesAndOutcomes/outcomes/retailerOutcome/retailerOutcome.scss";
import { StoreConditionDisplay } from "../../../../components/campaign/rulesAndOutcomes/outcomes/storeOutcome";
import "../../../../components/campaign/rulesAndOutcomes/outcomes/storeOutcome/storeOutcome.scss";
import { SurveyConditionDisplay } from "../../../../components/campaign/rulesAndOutcomes/outcomes/surveyOutcome";
import "../../../../components/campaign/rulesAndOutcomes/outcomes/surveyOutcome/surveyOutcome.scss";
import StreakConfigView from "../../../../components/campaign/viewComponents/StreakConfigView";
import ViewComponentsRenderer from "../../../../components/campaign/viewComponents/ViewComponentsRenderer";
import LeaderboardOutcomeSummary from "./LeaderboardOutcomeSummary";
import RewardAllocationOutcomeSummary from "./RewardAllocationOutcomeSummary";
import RewardGroupOutcomes from "./RewardGroupOutcomes";
import RewardOutcome from "./RewardOutcome";
import SummaryCard from "./SummaryCard";

const SURVEY_RULE_SLUG = "survey-answer-matching-rule";
const RETAILER_RULE_SLUG = "retailer-matching-rule";
const STORE_GROUP_RULE_SLUG = "store-group-matching-rule";

const CampaignOutcomes = ({
  isViewScreen,
  handleOutcomesEditSection,
  campaignStateData,
}) => {
  const [outcomes, setOutcomes] = useState([]);
  const rewardGroupsSlugs = [
    "participate-in-journey",
    "direct-rewards",
    "participate-in-journey-reward-selection",
  ];
  useEffect(() => {
    if (
      campaignStateData?.outcomes?.length &&
      campaignStateData?.outcomes?.[0]?.outcomeDefinition?.slug?.indexOf("journey") >
        -1 &&
      campaignStateData?.outcomes?.[0]?.outcomeData?.reward_group_ids?.length
    ) {
      setOutcomes(
        campaignStateData?.outcomes?.[0]?.outcomeData?.reward_group_ids?.map(
          (id) => ({
            outcomeData: { reward_group_id: id },
            outcomeDefinition: campaignStateData?.outcomes?.[0]?.outcomeDefinition,
            outcomeRules: [],
          })
        )
      );
    } else {
      setOutcomes(campaignStateData.outcomes);
    }
  }, [campaignStateData.outcomes]);

  return (
    <SummaryCard
      title="Campaign Setup - Outcomes"
      isViewScreen={isViewScreen}
      handleEditSection={handleOutcomesEditSection}
    >
      {outcomes?.length > 0 &&
        outcomes.map((outcome, index) =>
          rewardGroupsSlugs.indexOf(outcome?.outcomeDefinition?.slug) === -1 ? (
            <OutcomesRenderer
              outcome={outcome}
              key={outcome?.outcomeDefinition.id}
            />
          ) : (
            <RewardGroupsOutcomesRenderer
              campaignState={campaignStateData}
              outcome={outcome}
              key={outcome?.outcomeDefinition.id}
              index={index}
            />
          )
        )}
    </SummaryCard>
  );
};

const OutcomesRenderer = ({ outcome, key }) => {
  const surveyRuleData = useMemo(() => {
    const rules = outcome?.outcomeRules || [];
    const surveyRule = rules.find(
      (rule) => rule?.ruleDefinition?.slug === SURVEY_RULE_SLUG
    );
    return surveyRule?.ruleData || null;
  }, [outcome?.outcomeRules]);

  const retailerRuleData = useMemo(() => {
    const rules = outcome?.outcomeRules || [];
    const retailerRule = rules.find(
      (rule) => rule?.ruleDefinition?.slug === RETAILER_RULE_SLUG
    );
    return retailerRule?.ruleData || null;
  }, [outcome?.outcomeRules]);

  const storeRuleData = useMemo(() => {
    const rules = outcome?.outcomeRules || [];
    const storeRule = rules.find(
      (rule) => rule?.ruleDefinition?.slug === STORE_GROUP_RULE_SLUG
    );
    return storeRule?.ruleData || null;
  }, [outcome?.outcomeRules]);

  const hasCondition = !!surveyRuleData?.condition_display;
  const hasRetailerCondition =
    Array.isArray(retailerRuleData?.keywords) &&
    retailerRuleData.keywords.length > 0;
  const hasStoreCondition =
    !!storeRuleData?.store_configuration &&
    Array.isArray(storeRuleData?.keywords) &&
    storeRuleData.keywords.length > 0;

  const isDirectRewardsRedemption =
    outcome?.outcomeDefinition?.slug === "direct-redemption-rewards" ||
    outcome?.outcomeDefinition?.slug === "direct-redemption-rewards-b2b";

  const renderOutcomeContent = () => {
    const slug = outcome?.outcomeDefinition?.slug;

    if (slug === "reward-allocation-outcome") {
      return (
        <div className="additional-fields-container">
          <RewardAllocationOutcomeSummary outcome={outcome} key={outcome?.id} />
        </div>
      );
    }

    if (slug === "award-leaderboard-points") {
      return (
        <div className="additional-fields-container">
          <LeaderboardOutcomeSummary
            outcome={outcome}
            key={outcome?.outcomeData?.leaderboard_id}
          />
        </div>
      );
    }

    if (slug === "points-based-streaks") {
      return (
        <StreakConfigView
          data={outcome?.outcomeData}
          key={outcome?.outcomeData?.streak_config_id || outcome?.id}
        />
      );
    }

    if (isDirectRewardsRedemption) {
      return <DirectRewardsRedemptionOutcome outcome={outcome} />;
    }

    if (
      slug &&
      outcome?.outcomeDefinition?.inputSchema &&
      outcome?.outcomeDefinition?.uiSchema
    ) {
      return (
        <ViewComponentsRenderer
          customComponentName={slug}
          data={outcome?.outcomeData}
          inputSchema={outcome?.outcomeDefinition?.inputSchema}
          uiSchema={outcome?.outcomeDefinition?.uiSchema}
          definition={outcome}
        />
      );
    }

    return null;
  };

  return (
    <div className="outcome-card" key={key}>
      {renderOutcomeContent()}
      {hasCondition && <SurveyConditionDisplay ruleData={surveyRuleData} readOnly />}
      {hasRetailerCondition && (
        <RetailerConditionDisplay ruleData={retailerRuleData} readOnly />
      )}
      {hasStoreCondition && (
        <StoreConditionDisplay ruleData={storeRuleData} readOnly />
      )}
    </div>
  );
};

const RewardGroupsOutcomesRenderer = ({ outcome, campaignState, index }) => {
  return (
    <>
      {outcome?.outcomeDefinition?.slug === "direct-rewards" ? (
        <RewardOutcome outcome={outcome} key={outcome?.outcomeDefinition.id} />
      ) : (
        <RewardGroupOutcomes
          campaignType={campaignState.type}
          outcome={outcome}
          key={outcome?.outcomeData?.reward_group_id || index}
          campaignState={campaignState}
        />
      )}
    </>
  );
};

export default CampaignOutcomes;
